"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowDown, Package } from "lucide-react";

export default function InventoryRecipeScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Recipe ↔ inventory", "Receta ↔ inventario")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Sell a taco. ", "Vendes un taco. ")}
            <span className="text-redy-charcoal/30">{t("Decrement beef.", "Resta carne.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Every line on the receipt fires the recipe. Every recipe decrements ingredient stock. No \"weekly count\" guesswork — you know what you have at 11:47 PM after the rush.",
              "Cada línea del recibo dispara la receta. Cada receta resta del stock. Sin \"conteo semanal\" adivinando — sabes qué tienes a las 11:47 PM después del rush."
            )}
          </p>
        </motion.div>

        {/* Animated flow: receipt → recipe → inventory drop */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-6 items-center max-w-[920px] mx-auto"
        >
          {/* Receipt with sale */}
          <div className="bg-[#FAFAF8] border border-black/5 rounded-[20px] p-5">
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
              {t("Order #142", "Orden #142")}
            </div>
            <div
              className="space-y-1.5 text-[12px]"
              style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
            >
              <motion.div
                initial={{ backgroundColor: "rgba(244,114,43,0)" }}
                whileInView={{ backgroundColor: "rgba(244,114,43,0.12)" }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex justify-between rounded px-2 py-1 -mx-2 font-bold"
                style={{ color: "#F4722B" }}
              >
                <span>2× Birria Taco</span>
                <span className="tabular-nums">$9.00</span>
              </motion.div>
              <div className="flex justify-between text-redy-secondary">
                <span>1× Agua Fresca</span>
                <span className="tabular-nums">$4.00</span>
              </div>
              <div className="border-t border-dashed border-black/15 my-2" />
              <div className="flex justify-between font-bold text-redy-charcoal">
                <span>Total</span>
                <span className="tabular-nums">$13.00</span>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex md:flex-col items-center gap-2 py-4">
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                boxShadow: "0 10px 30px rgba(244,114,43,0.30)",
              }}
            >
              <ArrowDown className="w-5 h-5 text-white rotate-[-90deg] md:rotate-0" strokeWidth={3} />
            </motion.div>
            <div className="text-[10px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ color: "#D7263D" }}>
              {t("Recipe fires", "La receta dispara")}
            </div>
          </div>

          {/* Inventory side */}
          <div className="bg-white border border-black/5 rounded-[20px] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.04)]">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-3.5 h-3.5" style={{ color: "#F4722B" }} />
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                {t("Inventory after", "Inventario después")}
              </div>
            </div>

            <div className="space-y-3">
              {[
                { name: "Beef chuck", from: 14.3, to: 14.0, unit: "lb" },
                { name: "Corn tortillas", from: 287, to: 283, unit: "pcs" },
                { name: "Onion · cilantro", from: 4.2, to: 4.1, unit: "lb" },
                { name: "Salsa roja", from: 38, to: 36, unit: "oz" },
              ].map((stock, i) => (
                <motion.div
                  key={stock.name}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: 0.6 + 0.12 * i }}
                  className="text-[11px]"
                >
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="font-bold text-redy-charcoal">{stock.name}</span>
                    <span className="text-redy-secondary tabular-nums">
                      <span className="line-through opacity-60">{stock.from}</span>{" "}
                      <span className="font-black" style={{ color: "#F4722B" }}>
                        → {stock.to}
                      </span>{" "}
                      {stock.unit}
                    </span>
                  </div>
                  {/* Decrement bar */}
                  <div className="h-1 bg-[#FAFAF8] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: `${(stock.from / 20) * 100}%` }}
                      whileInView={{ width: `${(stock.to / 20) * 100}%` }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 0.8, delay: 0.8 + 0.12 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                      className="h-full rounded-full"
                      style={{ background: "linear-gradient(90deg, #F4722B 0%, #D7263D 100%)" }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
